import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, UnsupportedLocation } from '../../services/externalDataService.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-unsupported-geo-locations',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './unsupported-geo-locations.component.html',
  styleUrl: './unsupported-geo-locations.component.css'
})
export class UnsupportedGeoLocationsComponent implements OnInit {
  fromDate!: string;
  toDate!: string;
  loading = false;
  locations: UnsupportedLocation[] = [];

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;
  private map: google.maps.Map | null = null;
  private markers: google.maps.Marker[] = [];
  private infoWindow: google.maps.InfoWindow | null = null;

  constructor(private externalDataService: ExternalDataService) { }

  ngOnInit(): void {
    const today = new Date();
    // First day of current month in local time
    const start = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
    // Today end of day in local time
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    this.fromDate = new Date(start.getTime() - start.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    this.toDate = new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }

  async onSubmit(): Promise<void> {
    const fromIso = this.ensureUtcIso(this.fromDate, true);
    const toIso = this.ensureUtcIso(this.toDate, false);
    this.loading = true;
    this.externalDataService.getUnsupportedLocations(fromIso, toIso).subscribe({
      next: async (data) => {
        this.locations = data || [];
        await this.loadMapIfNeeded();
        this.renderMarkers();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        alert('Failed to load unsupported locations');
      }
    });
  }

  private ensureUtcIso(localDateString: string, isStartOfDay: boolean): string {
    // If already ISO with Z, return as is
    if (/Z$/.test(localDateString)) return localDateString;
    // Map YYYY-MM-DD input to a full local time string to be accurately parsed against UTC
    const timeSuffix = isStartOfDay ? 'T00:00:00' : 'T23:59:59';
    const dateStr = localDateString.length === 10 ? localDateString + timeSuffix : localDateString;
    return new Date(dateStr).toISOString();
  }

  private async loadMapIfNeeded(): Promise<void> {
    if (this.map) return;
    await this.loadGoogleMapsApi(environment.googleMapsApiKey);
    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: { lat: 20.5937, lng: 78.9629 },
      zoom: 5,
    });
    this.infoWindow = new google.maps.InfoWindow();
  }

  private renderMarkers(): void {
    if (!this.map) return;
    // Clear existing
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];

    if (!this.locations.length) return;

    const bounds = new google.maps.LatLngBounds();
    for (const loc of this.locations) {
      const position = { lat: loc.latitude, lng: loc.longitude };
      const marker = new google.maps.Marker({ position, map: this.map });
      marker.addListener('mouseover', () => {
        this.infoWindow?.setContent(`<div style="min-width:160px">${loc.phoneNumber}</div>`);
        this.infoWindow?.open({ anchor: marker, map: this.map! });
      });
      marker.addListener('mouseout', () => {
        this.infoWindow?.close();
      });
      this.markers.push(marker);
      bounds.extend(position);
    }

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  private loadGoogleMapsApi(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.maps) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps JS API'));
      document.head.appendChild(script);
    });
  }
}


